'use client';

import { c as _c } from "react/compiler-runtime";
import { ScrollArea as ArkScrollArea } from '@ark-ui/react/scroll-area';
import { cva, cx } from '@repo/panda/css';
import type { ReactNode } from 'react';
export interface ScrollAreaProps extends Omit<ArkScrollArea.RootProps, 'children'> {
  children: ReactNode;
  className?: string;
  viewportClassName?: string;
  contentClassName?: string;
  scrollbarClassName?: string;
  thumbClassName?: string;
  variant?: ScrollAreaVariants['variant'];
  size?: ScrollAreaVariants['size'];
  orientation?: 'horizontal' | 'vertical' | 'both';
}
type ScrollAreaVariants = {
  variant: 'default' | 'subtle' | 'minimal';
  size: 'sm' | 'md' | 'lg';
};
const scrollAreaRootStyles = cva({
  base: {
    position: 'relative',
    width: 'full',
    overflow: 'hidden'
  },
  variants: {
    variant: {
      default: {},
      subtle: {},
      minimal: {}
    },
    size: {
      sm: {},
      md: {},
      lg: {}
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
});
const scrollAreaViewportStyles = cva({
  base: {
    width: 'full',
    height: 'full'
  },
  variants: {
    variant: {
      default: {},
      subtle: {},
      minimal: {}
    },
    size: {
      sm: {},
      md: {},
      lg: {}
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
});
const scrollAreaContentStyles = cva({
  base: {
    minHeight: 'full',
    minWidth: 'full'
  },
  variants: {
    variant: {
      default: {},
      subtle: {},
      minimal: {}
    },
    size: {
      sm: {},
      md: {},
      lg: {}
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
});
const scrollAreaScrollbarStyles = cva({
  base: {
    display: 'flex',
    userSelect: 'none',
    touchAction: 'none',
    transition: 'all',
    // Match global scrollbar track styling
    backgroundColor: '[rgba(255,255,255,0.05)]',
    borderRadius: '8',
    padding: '1',
    // Ark keeps the scrollbar mounted and only flags whether its axis overflows.
    '&[data-orientation="vertical"]:not([data-overflow-y])': {
      display: 'none'
    },
    '&[data-orientation="horizontal"]:not([data-overflow-x])': {
      display: 'none'
    }
  },
  variants: {
    variant: {
      default: {
        '&[data-orientation="vertical"]': {
          width: '3'
        },
        '&[data-orientation="horizontal"]': {
          height: '3',
          flexDirection: 'column'
        }
      },
      subtle: {
        '&[data-orientation="vertical"]': {
          width: '2'
        },
        '&[data-orientation="horizontal"]': {
          height: '2',
          flexDirection: 'column'
        }
      },
      minimal: {
        '&[data-orientation="vertical"]': {
          width: '1'
        },
        '&[data-orientation="horizontal"]': {
          height: '1',
          flexDirection: 'column'
        }
      }
    },
    size: {
      sm: {},
      md: {},
      lg: {}
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
});
const scrollAreaThumbStyles = cva({
  base: {
    position: 'relative',
    flex: '1',
    // Match global scrollbar thumb styling
    borderRadius: '8',
    backgroundColor: '[rgba(255,255,255,0.20)]',
    transition: '[background-color]',
    '&:hover': {
      backgroundColor: '[rgba(255,255,255,0.30)]'
    },
    '&:active': {
      backgroundColor: '[rgba(255,255,255,0.40)]'
    },
    '&[data-orientation="vertical"]': {
      width: 'full'
    },
    '&[data-orientation="horizontal"]': {
      height: 'full'
    }
  },
  variants: {
    variant: {
      default: {},
      subtle: {},
      minimal: {}
    },
    size: {
      sm: {},
      md: {},
      lg: {}
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
});
export type ScrollAreaVariantsType = NonNullable<Parameters<typeof scrollAreaRootStyles>[0]>;
export const ScrollArea = t0 => {
  const $ = _c(52);
  let arkUIProps;
  let children;
  let className;
  let contentClassName;
  let scrollbarClassName;
  let t1;
  let t2;
  let t3;
  let thumbClassName;
  let viewportClassName;
  if ($[0] !== t0) {
    ({
      children,
      className,
      viewportClassName,
      contentClassName,
      scrollbarClassName,
      thumbClassName,
      variant: t1,
      size: t2,
      orientation: t3,
      ...arkUIProps
    } = t0);
    $[0] = t0;
    $[1] = arkUIProps;
    $[2] = children;
    $[3] = className;
    $[4] = contentClassName;
    $[5] = scrollbarClassName;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = thumbClassName;
    $[10] = viewportClassName;
  } else {
    arkUIProps = $[1];
    children = $[2];
    className = $[3];
    contentClassName = $[4];
    scrollbarClassName = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    thumbClassName = $[9];
    viewportClassName = $[10];
  }
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "md" : t2;
  const orientation = t3 === undefined ? "vertical" : t3;
  let t4;
  if ($[11] !== className || $[12] !== size || $[13] !== variant) {
    t4 = cx(scrollAreaRootStyles({
      variant,
      size
    }), className);
    $[11] = className;
    $[12] = size;
    $[13] = variant;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const rootClassName = t4;
  let t5;
  if ($[15] !== size || $[16] !== variant || $[17] !== viewportClassName) {
    t5 = cx(scrollAreaViewportStyles({
      variant,
      size
    }), viewportClassName);
    $[15] = size;
    $[16] = variant;
    $[17] = viewportClassName;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const viewportClass = t5;
  let t6;
  if ($[19] !== contentClassName || $[20] !== size || $[21] !== variant) {
    t6 = cx(scrollAreaContentStyles({
      variant,
      size
    }), contentClassName);
    $[19] = contentClassName;
    $[20] = size;
    $[21] = variant;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const contentClass = t6;
  let t7;
  if ($[23] !== scrollbarClassName || $[24] !== size || $[25] !== variant) {
    t7 = cx(scrollAreaScrollbarStyles({
      variant,
      size
    }), scrollbarClassName);
    $[23] = scrollbarClassName;
    $[24] = size;
    $[25] = variant;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const scrollbarClass = t7;
  let t8;
  if ($[27] !== size || $[28] !== thumbClassName || $[29] !== variant) {
    t8 = cx(scrollAreaThumbStyles({
      variant,
      size
    }), thumbClassName);
    $[27] = size;
    $[28] = thumbClassName;
    $[29] = variant;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const thumbClass = t8;
  const showVertical = orientation === "vertical" || orientation === "both";
  const showHorizontal = orientation === "horizontal" || orientation === "both";
  let t9;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      minWidth: "0"
    };
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== children || $[33] !== contentClass) {
    t10 = <ArkScrollArea.Content className={contentClass} style={t9}>{children}</ArkScrollArea.Content>;
    $[32] = children;
    $[33] = contentClass;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== viewportClass) {
    t11 = <ArkScrollArea.Viewport className={viewportClass}>{t10}</ArkScrollArea.Viewport>;
    $[35] = t10;
    $[36] = viewportClass;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== scrollbarClass || $[39] !== showVertical || $[40] !== thumbClass) {
    t12 = showVertical && <ArkScrollArea.Scrollbar orientation="vertical" className={scrollbarClass}><ArkScrollArea.Thumb className={thumbClass} /></ArkScrollArea.Scrollbar>;
    $[38] = scrollbarClass;
    $[39] = showVertical;
    $[40] = thumbClass;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== scrollbarClass || $[43] !== showHorizontal || $[44] !== thumbClass) {
    t13 = showHorizontal && <ArkScrollArea.Scrollbar orientation="horizontal" className={scrollbarClass}><ArkScrollArea.Thumb className={thumbClass} /></ArkScrollArea.Scrollbar>;
    $[42] = scrollbarClass;
    $[43] = showHorizontal;
    $[44] = thumbClass;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== arkUIProps || $[47] !== rootClassName || $[48] !== t11 || $[49] !== t12 || $[50] !== t13) {
    t14 = <ArkScrollArea.Root className={rootClassName} {...arkUIProps}>{t11}{t12}{t13}</ArkScrollArea.Root>;
    $[46] = arkUIProps;
    $[47] = rootClassName;
    $[48] = t11;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  return t14;
};